import React, { useState } from "react";
import imgCHIQ    from "../assets/partners/CHIQ.png";
import imgGREE    from "../assets/partners/GREE.png";
import imgHisense from "../assets/partners/hisence.png";
import imgLG      from "../assets/partners/LG.png";
import imgMidea   from "../assets/partners/Midea.png";
import imgSamsung from "../assets/partners/samsung.png";
import imgTrane   from "../assets/partners/tran.png";
import { motion, AnimatePresence } from "framer-motion";
import heroBg from "../assets/hero-01.png";
import { Link } from "react-router-dom";

const industries = [
  {
    id: "commercial",
    icon: "🏢",
    title: "COMMERCIAL",
    subtitle: "Offices • Banks • Malls • Supermarkets",
    typicalClients: ["Corporate offices & business parks.", "Banking & financial institutions.", "Shopping malls & supermarkets.", "Mixed-use commercial developments."],
    stats: [{ value: "30%", label: "Avg energy savings with Inverter AC" }, { value: "24/7", label: "Emergency support available" }],
    delivers: [
      { title: "Centralized Air Conditioning Systems", desc: "VRF/VRV and centralized HVAC for large open-plan offices and multi-floor buildings." },
      { title: "Precision Temperature & Humidity Control", desc: "Consistent climate conditions for staff productivity and equipment protection." },
      { title: "Energy Management & Efficiency", desc: "Inverter-based systems and smart controls to reduce operating costs significantly." },
      { title: "Preventive Maintenance Contracts", desc: "Scheduled servicing to ensure zero downtime and protect your HVAC investment." },
    ],
    brands: ["LG", "Midea", "Gree", "Trane"],
  },
  {
    id: "hospitality",
    icon: "🏨",
    title: "HOSPITALITY",
    subtitle: "Hotels • Resorts • Restaurants • Conference Centres",
    typicalClients: ["Hotels and boutique lodges.", "Resorts and conference centres.", "Restaurants and food service venues.", "Guesthouses and serviced apartments."],
    stats: [{ value: "★★★★", label: "5-star comfort standards" }, { value: "Silent", label: "Low-noise systems for guest rooms" }],
    delivers: [
      { title: "Guest Room Climate Systems", desc: "Silent, energy-efficient split and cassette AC for superior guest comfort and quiet operation." },
      { title: "Lobby & Common Area Cooling", desc: "Centralized or VRF systems for grand lobbies, restaurants, and event spaces." },
      { title: "Kitchen & Laundry Ventilation", desc: "Commercial ventilation and exhaust systems meeting food safety and hygiene standards." },
      { title: "Ongoing Maintenance Programs", desc: "Regular servicing to protect your guest experience and avoid costly breakdowns." },
    ],
    brands: ["LG", "Trane", "Hisense", "Midea"],
  },
  {
    id: "healthcare",
    icon: "🏥",
    title: "HEALTHCARE",
    subtitle: "Hospitals • Clinics • Laboratories • Pharmacies",
    typicalClients: ["Hospitals and surgical centres.", "Medical laboratories and diagnostic centres.", "Clinics and outpatient facilities.", "Pharmacies and pharmaceutical storage."],
    stats: [{ value: "24/7", label: "On-call emergency response" }, { value: "±0.5°C", label: "Precision temperature control" }],
    delivers: [
      { title: "Precision Climate Control for Sterile Environments", desc: "Tightly controlled temperature and humidity to meet infection control and clinical standards." },
      { title: "Pharmaceutical Cold Storage HVAC", desc: "Reliable cooling for medication storage rooms, blood banks, and specimen areas." },
      { title: "Negative & Positive Pressure Rooms", desc: "Specialized ventilation for isolation wards, operating theatres, and ICU units." },
      { title: "24/7 Reliability & Emergency Response", desc: "Critical environment HVAC cannot fail — our rapid response keeps systems running always." },
    ],
    brands: ["Trane", "LG", "Gree"],
  },
  {
    id: "residential",
    icon: "🏠",
    title: "RESIDENTIAL",
    subtitle: "Homes • Apartments • Estates • Condominiums",
    typicalClients: ["Private homes and family residences.", "Apartment complexes and condominiums.", "Gated estates and housing developments.", "Rental properties and managed housing."],
    stats: [{ value: "UGX 2M", label: "Residential units from as low as" }, { value: "2 Hrs", label: "Average residential installation time" }],
    delivers: [
      { title: "Split & Multi-Split Air Conditioning", desc: "Efficient, affordable individual room cooling with inverter technology for lower electricity bills." },
      { title: "Whole-Home Ducted Systems", desc: "Centralized ducted HVAC for consistent comfort throughout your entire home." },
      { title: "Expert Installation & Setup", desc: "Clean, professional installation with full system setup, testing, and client handover briefing." },
      { title: "Affordable Maintenance Packages", desc: "Annual and bi-annual servicing packages designed to keep your system efficient year-round." },
    ],
    brands: ["Hisense", "Chiq", "LG", "Midea"],
  },
  {
    id: "industrial",
    icon: "🏭",
    title: "INDUSTRIAL",
    subtitle: "Factories • Cold Rooms • Data Centres • Warehouses",
    typicalClients: ["Manufacturing facilities and factories.", "Cold room and cold chain storage.", "Server rooms and data centres.", "Warehouses and distribution centres."],
    stats: [{ value: "N+1", label: "Redundant cooling for critical sites" }, { value: "−20°C", label: "Cold room design capability" }],
    delivers: [
      { title: "Industrial Process Cooling", desc: "Heavy-duty HVAC designed for continuous operation in demanding manufacturing environments." },
      { title: "Cold Room & Refrigeration Systems", desc: "Commercial refrigeration design, installation, and maintenance for cold chain integrity." },
      { title: "Server Room Precision Air Conditioning", desc: "Dedicated PAC units and redundant cooling systems protecting your critical IT infrastructure." },
      { title: "Industrial Ventilation Systems", desc: "Extract, supply, and recirculation ventilation managing heat, fumes, and air quality." },
    ],
    brands: ["Trane", "Gree", "Midea"],
  },
  {
    id: "education",
    icon: "🎓",
    title: "EDUCATION",
    subtitle: "Universities • Schools • Training Institutes • Labs",
    typicalClients: ["Universities and higher education institutions.", "Primary and secondary schools.", "Technical and vocational training institutes.", "Computer labs and science laboratories."],
    stats: [{ value: "Quiet", label: "Low-noise for learning environments" }, { value: "Smart", label: "Programmable schedule controls" }],
    delivers: [
      { title: "Classroom & Lecture Hall Cooling", desc: "Comfortable learning environments with energy-efficient systems suitable for large student populations." },
      { title: "Library & Study Space Climate Control", desc: "Quiet, consistent cooling for reading rooms, study halls, and resource centres." },
      { title: "Laboratory Ventilation & AC", desc: "Specialized ventilation for science labs, computer rooms, and technical workshops." },
      { title: "Budget-Conscious Solutions", desc: "Cost-effective brands and financing-friendly packages designed for institutional budgets." },
    ],
    brands: ["Midea", "Gree", "Hisense", "Chiq"],
  },
  {
    id: "government",
    icon: "🏛️",
    title: "GOVERNMENT",
    subtitle: "Ministries • Embassies • Public Buildings • Offices",
    typicalClients: ["Government ministries and departments.", "Embassies and diplomatic missions.", "Municipal and local government offices.", "Courts and public administration buildings."],
    stats: [{ value: "100%", label: "Registered & compliant company" }, { value: "BOQ", label: "Full tender docs prepared" }],
    delivers: [
      { title: "Large-Scale HVAC Design & Installation", desc: "Full engineering design and implementation for multi-floor government complexes and public buildings." },
      { title: "Compliance & Certification", desc: "Fully registered, compliant installations meeting all applicable Ugandan standards and specifications." },
      { title: "Long-Term Maintenance Contracts", desc: "Structured service agreements ensuring continuous operation of mission-critical government facilities." },
      { title: "Tender-Ready Technical Proposals", desc: "Professional BOQs, specifications, and engineering drawings for procurement processes." },
    ],
    brands: ["Trane", "LG", "Gree", "Midea"],
  },
  {
    id: "construction",
    icon: "🏗️",
    title: "CONSTRUCTION",
    subtitle: "Developers • Architects • Contractors • Consultants",
    typicalClients: ["Real estate developers and property investors.", "Architects and interior design firms.", "General contractors and M&E contractors.", "Engineering consultancies and project managers."],
    stats: [{ value: "BSc.", label: "Engineering-grade design capability" }, { value: "BOQ+", label: "Full design docs & specifications" }],
    delivers: [
      { title: "HVAC Design & Engineering Consultancy", desc: "Load calculations, equipment selection, duct design, and engineering drawings from first principles." },
      { title: "Bills of Quantities & Specifications", desc: "Accurate BOQs and technical specifications for inclusion in tender documents and project budgets." },
      { title: "Project Supervision & Commissioning", desc: "On-site supervision of HVAC installation by qualified engineers, through to formal commissioning." },
      { title: "Preferred Installer Partnerships", desc: "Long-term preferred installer relationships with developers for ongoing project pipelines." },
    ],
    brands: ["All Brands Per Project Specified"],
  },
];

const brandImages = {
  LG:      imgLG,
  Midea:   imgMidea,
  Gree:    imgGREE,
  Trane:   imgTrane,
  Hisense: imgHisense,
  Samsung: imgSamsung,
  Chiq:    imgCHIQ,
};

export default function IndustriesPage() {
  const [active, setActive] = useState("commercial");
  const industry = industries.find((i) => i.id === active);

  return (
    <motion.div
      className="font-poppins"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >

      {/* ── HERO ── */}
      <section className="relative min-h-[300px] sm:min-h-[380px] lg:min-h-[450px] flex items-center justify-center text-white overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${heroBg})` }} />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(0, 199, 239, 0.55)" }} />
        <div className="relative text-center px-4 sm:px-8 lg:px-16 py-14 sm:py-20">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4" style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.8)" }}>
            Industries We Serve
          </h1>
          <p className="text-base sm:text-lg font-semibold uppercase tracking-widest max-w-2xl mx-auto text-white leading-snug" style={{ textShadow: "1px 1px 6px rgba(0,0,0,0.9)" }}>
            Engineered HVAC Solutions Across Every Sector
          </p>
        </div>
      </section>

      {/* ── INDUSTRY TABS — slides down from top ── */}
      <motion.div
        style={{ backgroundColor: "#0F1030" }}
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex overflow-x-auto scrollbar-hide">
            {industries.map((ind, i) => (
              <motion.button
                key={ind.id}
                onClick={() => setActive(ind.id)}
                className="flex-shrink-0 flex items-center gap-2 px-4 sm:px-5 py-4 text-xs sm:text-sm font-bold tracking-wide transition-colors duration-150 border-b-4 whitespace-nowrap"
                style={{
                  borderBottomColor: active === ind.id ? "#00C7EF" : "transparent",
                  color: active === ind.id ? "#00C7EF" : "rgba(255,255,255,0.5)",
                }}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.3 + i * 0.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-base">{ind.icon}</span>
                <span>{ind.title}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </motion.div>

      {/* ── INDUSTRY DETAIL ── */}
      <section className="py-8 sm:py-12" style={{ backgroundColor: "#e8f4f8" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* AnimatePresence re-animates when tab changes */}
          <AnimatePresence mode="wait">
            <motion.div
              key={active}
              className="grid grid-cols-1 lg:grid-cols-3 gap-0 overflow-hidden shadow-lg"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >

              {/* ── LEFT PANEL — slides in from left ── */}
              <motion.div
                className="lg:col-span-1 flex flex-col"
                style={{ backgroundColor: "#0d1535" }}
                initial={{ opacity: 0, x: -60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                {/* Industry header */}
                <div className="px-6 pt-7 pb-5 flex items-start gap-4" style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                  <div
                    className="flex-shrink-0 w-14 h-14 rounded-full flex items-center justify-center text-2xl"
                    style={{ backgroundColor: "rgba(0,199,239,0.15)", border: "2px solid #00C7EF" }}
                  >
                    {industry.icon}
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold tracking-widest leading-tight" style={{ color: "#fff" }}>
                      {industry.title}
                    </h2>
                    <p className="text-xs mt-1 leading-relaxed" style={{ color: "#00C7EF" }}>
                      {industry.subtitle.split(" • ").map((s, i, arr) => (
                        <span key={s}>• {s}{i < arr.length - 1 ? " " : ""}</span>
                      ))}
                    </p>
                  </div>
                </div>

                {/* Typical clients — stagger in */}
                <div className="px-6 py-5 flex-1">
                  <p className="text-xs font-extrabold tracking-[0.2em] uppercase mb-4" style={{ color: "#00C7EF" }}>
                    Typical Clients
                  </p>
                  <ul className="space-y-3">
                    {industry.typicalClients.map((c, i) => (
                      <motion.li
                        key={c}
                        className="flex items-start gap-3 text-sm"
                        style={{ color: "rgba(255,255,255,0.85)" }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 + i * 0.08 }}
                      >
                        <span className="flex-shrink-0 mt-1.5 w-2 h-2 rounded-full" style={{ backgroundColor: "#00C7EF" }} />
                        {c}
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Stats — pop in */}
                <div className="grid grid-cols-2" style={{ borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  {industry.stats.map((s, i) => (
                    <motion.div
                      key={s.value}
                      className="flex flex-col items-center justify-center py-6 px-4 text-center"
                      style={{ borderRight: i === 0 ? "1px solid rgba(255,255,255,0.1)" : "none" }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4, ease: "easeOut", delay: 0.3 + i * 0.1 }}
                    >
                      <p className="text-2xl sm:text-3xl font-extrabold leading-none" style={{ color: "#00C7EF" }}>
                        {s.value}
                      </p>
                      <p className="text-xs mt-2 leading-snug" style={{ color: "rgba(255,255,255,0.6)" }}>
                        {s.label}
                      </p>
                    </motion.div>
                  ))}
                </div>

                {/* CTA */}
                <Link
                  to="/contact"
                  className="block text-center font-bold text-sm py-4 tracking-widest uppercase transition-colors duration-150"
                  style={{ backgroundColor: "#00C7EF", color: "#0F1030" }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#00C7EF"; }}
                >
                  Get a Quote →
                </Link>
              </motion.div>

              {/* ── RIGHT PANEL — slides in from right ── */}
              <motion.div
                className="lg:col-span-2 flex flex-col"
                style={{ backgroundColor: "#daeef6" }}
                initial={{ opacity: 0, x: 60 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              >
                {/* Header */}
                <div className="px-7 pt-7 pb-4">
                  <p className="text-xs font-extrabold tracking-[0.2em] uppercase" style={{ color: "#0F1030" }}>
                    What We Deliver For You
                  </p>
                  <div className="w-12 h-0.5 mt-2" style={{ backgroundColor: "#00C7EF" }} />
                </div>

                {/* Deliver items — stagger up one by one */}
                <div className="px-7 pb-6 flex flex-col gap-5 flex-1">
                  {industry.delivers.map((d, i) => (
                    <motion.div
                      key={d.title}
                      className="flex items-start gap-4"
                      initial={{ opacity: 0, y: 25 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: "easeOut", delay: 0.1 + i * 0.1 }}
                    >
                      <div
                        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                        style={{ backgroundColor: "#00C7EF" }}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-extrabold text-sm sm:text-base leading-snug" style={{ color: "#0d1535" }}>
                          {d.title}
                        </p>
                        <p className="text-xs sm:text-sm mt-1 leading-relaxed" style={{ color: "#374151" }}>
                          {d.desc}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Brands — pop in one by one */}
                {/* <div className="px-7 py-5" style={{ borderTop: "1px solid rgba(13,21,53,0.15)" }}>
                  <p className="text-xs font-extrabold tracking-[0.2em] uppercase mb-4" style={{ color: "#0F1030" }}>
                    Recommended Brands
                  </p>
                  <div className="flex flex-wrap gap-4 items-center">
                    {industry.brands.map((b, i) => (
                      <motion.div
                        key={b}
                        className="flex items-center justify-center bg-white rounded p-2"
                        style={{ minWidth: "72px", height: "48px" }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, ease: "easeOut", delay: 0.4 + i * 0.08 }}
                      >
                        {brandImages[b] ? (
                          <img
                            src={brandImages[b]}
                            alt={b}
                            className="max-h-8 w-auto object-contain"
                            style={{ maxWidth: "80px" }}
                          />
                        ) : (
                          <span className="text-xs font-bold px-2 text-center leading-tight" style={{ color: "#0F1030" }}>{b}</span>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div> */}
              </motion.div>

            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── BOTTOM CTA — fades up ── */}
      <motion.section
        className="py-10 sm:py-14"
        style={{ backgroundColor: "#f9fafb" }}
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.4 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="py-12 px-6 text-center" style={{ backgroundColor: "#0F1030", border: "2px solid #00C7EF" }}>
            <h2 className="text-2xl sm:text-3xl font-extrabold mb-3" style={{ color: "#00C7EF" }}>
              Your Industry, Our Expertise
            </h2>
            <p className="text-gray-300 text-sm sm:text-base mb-6 max-w-xl mx-auto">
              Tell us about your project and we'll recommend the right HVAC solution for your specific sector and budget.
            </p>
            <Link
              to="/contact"
              className="inline-block font-bold px-8 py-3 text-sm tracking-wide transition-colors duration-150"
              style={{ backgroundColor: "#00C7EF", color: "#0F1030" }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#fff"; e.currentTarget.style.color = "#0F1030"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "#00C7EF"; e.currentTarget.style.color = "#0F1030"; }}
            >
              DISCUSS YOUR PROJECT
            </Link>
          </div>
        </div>
      </motion.section>

    </motion.div>
  );
}